import {
  useCallback,
  useRef,
  useEffect,
  useState,
  createContext,
  useContext,
  memo,
  ReactNode,
  JSX,
} from 'react';
import useAuth from 'src/hooks/useAuth';
import { NotificationProvider, useNotifications } from './NotificationContext';
import { WatchListProvider, useWatchList } from './WatchListContext';

interface RealTimeContextType {
  // Notifications
  notifications: ReturnType<typeof useNotifications>['notifications'];
  unreadCount: ReturnType<typeof useNotifications>['unreadCount'];
  notificationLoading: ReturnType<typeof useNotifications>['loading'];
  notificationError: ReturnType<typeof useNotifications>['error'];
  wsError: ReturnType<typeof useNotifications>['wsError'];
  wsConnected: ReturnType<typeof useNotifications>['wsConnected'];

  // Watch List
  watchedCerts: ReturnType<typeof useWatchList>['watchedCerts'];
  watchedCertsCount: ReturnType<typeof useWatchList>['watchedCertsCount'];
  watchedCertsLimit: ReturnType<typeof useWatchList>['watchedCertsLimit'];
  watchListLoading: ReturnType<typeof useWatchList>['loading'];
  watchListUpdating: ReturnType<typeof useWatchList>['updating'];
  watchListError: ReturnType<typeof useWatchList>['error'];
  selectedWatchedIds: ReturnType<typeof useWatchList>['selectedWatchedIds'];

  // Shared
  permissions: {
    canUseNotifications: boolean;
    canUseWatchList: boolean;
  };

  // Notification Actions
  fetchNotifications: ReturnType<typeof useNotifications>['fetchNotifications'];
  markAsRead: ReturnType<typeof useNotifications>['markAsRead'];
  markAsUnread: ReturnType<typeof useNotifications>['markAsUnread'];
  markAllAsRead: ReturnType<typeof useNotifications>['markAllAsRead'];
  bulkMarkAsRead: ReturnType<typeof useNotifications>['bulkMarkAsRead'];
  bulkMarkAsUnread: ReturnType<typeof useNotifications>['bulkMarkAsUnread'];

  // Watch List Actions
  fetchWatchedCerts: ReturnType<typeof useWatchList>['fetchWatchedCerts'];
  handleRemoveWatch: ReturnType<typeof useWatchList>['handleRemoveWatch'];
  handleBatchRemove: ReturnType<typeof useWatchList>['handleBatchRemove'];
  handleAddToWatchList: ReturnType<typeof useWatchList>['handleAddToWatchList'];

  // Selection Actions
  handleToggleSelection: ReturnType<
    typeof useWatchList
  >['handleToggleSelection'];
  handleSelectAll: ReturnType<typeof useWatchList>['handleSelectAll'];
  handleClearSelection: ReturnType<typeof useWatchList>['handleClearSelection'];
}

// Create context
const RealTimeContext = createContext<RealTimeContextType | undefined>(
  undefined
);

// Hook to use the context
export const useRealTime = (): RealTimeContextType => {
  const context = useContext(RealTimeContext);
  if (!context) {
    throw new Error('useRealTime must be used within a RealTimeProvider');
  }
  return context;
};

// Inner component that uses both contexts and provides polling
const RealTimeInner = memo(
  ({ children }: { children: ReactNode }): JSX.Element => {
    const { user } = useAuth();
    const notifications = useNotifications();
    const watchList = useWatchList();

    // Tab focus state
    const [isTabActive, setIsTabActive] = useState<boolean>(!document.hidden);

    // Simple polling manager
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isActiveRef = useRef(true);
    const effectRunRef = useRef(false);

    // User and permissions
    const canUseNotifications =
      user?.canUseRealTimeNotificationsFileOwner ||
      user?.canUseRealTimeNotificationsWatched ||
      user?.canUseRealTimeNotificationsProperty;
    const canUseWatchList = user?.canUseRealTimeNotificationsWatched;

    // WebSocket fallback logic - use polling if WebSocket fails permanently
    const shouldUsePolling =
      !user?.canUseWebSockets ||
      (user?.canUseWebSockets &&
        notifications.wsError &&
        !notifications.wsConnected);

    const startPolling = useCallback(() => {
      if (intervalRef.current) {
        return;
      }

      intervalRef.current = setInterval(async () => {
        if (!isActiveRef.current) {
          return;
        }

        try {
          if (canUseWatchList) {
            await watchList.fetchWatchedCerts(false);
          }
          if (shouldUsePolling && canUseNotifications) {
            await notifications.fetchNotifications(false);
          }
        } catch {
          // Silent error handling for polling
        }
      }, 15000);
    }, [
      canUseWatchList,
      watchList,
      canUseNotifications,
      notifications,
      shouldUsePolling,
    ]);

    const stopPolling = useCallback(() => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, []);

    // Tab focus detection with simple polling control
    useEffect(() => {
      // Don't set up listeners until user data is loaded
      if (
        !user ||
        canUseNotifications === undefined ||
        canUseWatchList === undefined
      ) {
        return;
      }

      const handleVisibilityChange = (): void => {
        const isActive = !document.hidden;
        setIsTabActive(isActive);
        isActiveRef.current = isActive;

        if (isActive && (canUseWatchList || shouldUsePolling)) {
          // Tab became active, start polling immediately
          startPolling();
        } else {
          // Tab became inactive, stop polling
          stopPolling();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );
      };
    }, [
      user,
      canUseNotifications,
      canUseWatchList,
      startPolling,
      stopPolling,
      shouldUsePolling,
    ]);

    // Unified polling effect
    useEffect(() => {
      // Don't run until user data is loaded
      if (
        !user ||
        canUseNotifications === undefined ||
        canUseWatchList === undefined
      ) {
        return;
      }

      // Only do initial fetch once
      if (!effectRunRef.current) {
        // Initial fetch
        if (canUseNotifications) {
          notifications.fetchNotifications(true);
        }
        if (canUseWatchList) {
          watchList.fetchWatchedCerts(true);
        }

        effectRunRef.current = true;
      }

      // Always start/stop polling based on tab activity
      if (isTabActive && (canUseWatchList || shouldUsePolling)) {
        startPolling();
      } else {
        stopPolling();
      }

      return () => {
        stopPolling();
      };
    }, [
      user,
      canUseNotifications,
      canUseWatchList,
      isTabActive,
      startPolling,
      stopPolling,
      notifications,
      watchList,
      shouldUsePolling,
    ]);

    const value: RealTimeContextType = {
      // Notifications
      notifications: notifications.notifications,
      unreadCount: notifications.unreadCount,
      notificationLoading: notifications.loading,
      notificationError: notifications.error,
      wsConnected: notifications.wsConnected,
      wsError: notifications.wsError,

      // Watch List
      watchedCerts: watchList.watchedCerts,
      watchedCertsCount: watchList.watchedCertsCount,
      watchedCertsLimit: watchList.watchedCertsLimit,
      watchListLoading: watchList.loading,
      watchListUpdating: watchList.updating,
      watchListError: watchList.error,
      selectedWatchedIds: watchList.selectedWatchedIds,

      // Shared
      permissions: {
        canUseNotifications,
        canUseWatchList,
      },

      // Notification Actions
      fetchNotifications: notifications.fetchNotifications,
      markAsRead: notifications.markAsRead,
      markAsUnread: notifications.markAsUnread,
      markAllAsRead: notifications.markAllAsRead,
      bulkMarkAsRead: notifications.bulkMarkAsRead,
      bulkMarkAsUnread: notifications.bulkMarkAsUnread,

      // Watch List Actions
      fetchWatchedCerts: watchList.fetchWatchedCerts,
      handleRemoveWatch: watchList.handleRemoveWatch,
      handleBatchRemove: watchList.handleBatchRemove,
      handleAddToWatchList: watchList.handleAddToWatchList,

      // Selection Actions
      handleToggleSelection: watchList.handleToggleSelection,
      handleSelectAll: watchList.handleSelectAll,
      handleClearSelection: watchList.handleClearSelection,
    };

    return (
      <RealTimeContext.Provider value={value}>
        {children}
      </RealTimeContext.Provider>
    );
  }
);

RealTimeInner.displayName = 'RealTimeInner';

// Main provider component
export const RealTimeProvider = memo(
  ({ children }: { children: ReactNode }): JSX.Element => {
    return (
      <NotificationProvider>
        <WatchListProvider>
          <RealTimeInner>{children}</RealTimeInner>
        </WatchListProvider>
      </NotificationProvider>
    );
  }
);

RealTimeProvider.displayName = 'RealTimeProvider';
